import type { EstadoTrabajo } from '@laser/core';
import type { FiltrosApiTrabajos, TrabajoResumen } from '../api/tipos';
import { ESTADOS } from './estados';
import { aFechaInput } from './fechas';

export type Orden = 'compromiso_asc' | 'compromiso_desc';

export interface FiltrosTrabajos extends Omit<FiltrosApiTrabajos, 'limite' | 'cursor'> {
  orden?: Orden;
}

const CLAVES_TEXTO = ['maquinaId', 'materialId', 'clienteId', 'q'] as const;
const FECHA = /^\d{4}-\d{2}-\d{2}$/;

export function filtrosDesdeParams(p: URLSearchParams): FiltrosTrabajos {
  const f: FiltrosTrabajos = {};
  const estado = p.get('estado');
  if (estado && (ESTADOS as string[]).includes(estado)) f.estado = estado as EstadoTrabajo;
  for (const k of CLAVES_TEXTO) {
    const v = p.get(k)?.trim();
    if (v) f[k] = v;
  }
  const desde = p.get('desde'), hasta = p.get('hasta');
  if (desde && FECHA.test(desde)) f.desde = desde;
  if (hasta && FECHA.test(hasta)) f.hasta = hasta;
  const urg = p.get('urgente');
  if (urg === '1' || urg === 'true') f.urgente = true;
  if (urg === '0' || urg === 'false') f.urgente = false;
  if (p.get('orden') === 'compromiso_desc') f.orden = 'compromiso_desc';
  return f;
}

export function filtrosAParams(f: FiltrosTrabajos): URLSearchParams {
  const p = new URLSearchParams();
  if (f.estado) p.set('estado', f.estado);
  for (const k of CLAVES_TEXTO) {
    const v = f[k]?.trim();
    if (v) p.set(k, v);
  }
  if (f.desde) p.set('desde', f.desde);
  if (f.hasta) p.set('hasta', f.hasta);
  if (f.urgente !== undefined) p.set('urgente', f.urgente ? '1' : '0');
  if (f.orden === 'compromiso_desc') p.set('orden', f.orden);
  return p;
}

export const hayFiltros = (f: FiltrosTrabajos) =>
  Boolean(f.estado || f.maquinaId || f.materialId || f.clienteId || f.q || f.desde || f.hasta || f.urgente !== undefined);

const sinAcentos = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Mismo filtro que la api (lo usa el mock y sirve de referencia). `desde/hasta` = fecha de entrada en CDMX, inclusive. */
export function filtrarTrabajos(lista: TrabajoResumen[], f: FiltrosTrabajos, opciones: { ignorarEstado?: boolean } = {}): TrabajoResumen[] {
  const q = f.q ? sinAcentos(f.q.trim()) : '';
  const qDigitos = (f.q ?? '').replace(/\D/g, '');
  return lista.filter((t) => {
    if (!opciones.ignorarEstado && f.estado && t.estado !== f.estado) return false;
    if (f.maquinaId) {
      const maq = t.maquinaAsignadaId ?? (t.cotizacionFinal ?? t.cotizacionOriginal).maquinaId;
      if (maq !== f.maquinaId) return false;
    }
    if (f.materialId && t.solicitud.materialId !== f.materialId) return false;
    if (f.clienteId && t.clienteId !== f.clienteId) return false;
    if (f.urgente !== undefined && t.urgente !== f.urgente) return false;
    if (f.desde || f.hasta) {
      const dia = aFechaInput(t.creadoEn);
      if (f.desde && dia < f.desde) return false;
      if (f.hasta && dia > f.hasta) return false;
    }
    if (q) {
      const c = t.cliente;
      const texto = sinAcentos([t.folio, c?.nombre, c?.correo, c?.telefono].filter(Boolean).join(' '));
      const tel = (c?.telefono ?? '').replace(/\D/g, '');
      if (!texto.includes(q) && !(qDigitos.length >= 4 && tel.includes(qDigitos))) return false;
    }
    return true;
  });
}

/** Por fecha compromiso; urgentes y prioridad manual no alteran el orden aquí (eso es del programa). */
export function ordenarPorCompromiso<T extends { fechaCompromiso: string; folio: string }>(lista: T[], orden: Orden = 'compromiso_asc'): T[] {
  const s = orden === 'compromiso_desc' ? -1 : 1;
  return [...lista].sort((a, b) => {
    const d = Date.parse(a.fechaCompromiso) - Date.parse(b.fechaCompromiso);
    return d !== 0 ? d * s : a.folio.localeCompare(b.folio);
  });
}

export function contarPorEstado(lista: { estado: EstadoTrabajo }[]): Record<EstadoTrabajo, number> {
  const c = Object.fromEntries(ESTADOS.map((e) => [e, 0])) as Record<EstadoTrabajo, number>;
  for (const t of lista) c[t.estado]++;
  return c;
}
